/**
 * Fetches the shipping cost after the checkout has painted (WBHB-9722, AK9).
 *
 * The page never waits on the Bold & Epic interface: the adjuster reads the cache
 * only, so a cart nobody has quoted yet renders as `pending`, with the payment methods
 * replaced by a spinner. This asks for the quote, then repaints the shipping row, the
 * total, the tax, and the payment methods.
 *
 * Three end states, and the payment methods follow from them:
 *   - `priceable`   the amount is shown and every gateway is available;
 *   - `on_request`  only Vorkasse is offered, with no explanation next to it: the
 *                   shipping row in the summary already says the cost follows (AK12);
 *   - `none`        the cart holds nothing shippable, so nothing is withheld (AK13).
 *
 * On a non-production environment the block also carries two debug buttons, so the
 * pending state and the on-request state can be seen without breaking the interface.
 */

interface ShippingQuoteResponse {
    state: 'none' | 'pending' | 'priceable' | 'on_request';
    onRequest: boolean;
    pending: boolean;
    forcedOnRequest: boolean;
    shippingLabel: string;
    shippingVatLabel: string;
    shippingDescriptions: string[];
    total: string;
    totalTax: string;
}

export default class CheckoutShippingQuote {
    private container: HTMLElement | null = null;
    private loading: HTMLElement | null = null;
    private options: HTMLElement | null = null;
    private shippingPrice: HTMLElement | null = null;
    private shippingVat: HTMLElement | null = null;
    private total: HTMLElement | null = null;
    private totalTax: HTMLElement | null = null;
    private debugState: HTMLElement | null = null;

    /**
     * Called by the orchestrator on every page, so it leaves immediately unless the
     * checkout's payment block is on this one.
     */
    public init(): void {
        this.container = document.querySelector('.js-payment-methods');

        if (!this.container) {
            return;
        }

        this.loading = document.querySelector('.js-payment-loading');
        this.options = document.querySelector('.js-payment-options');
        this.shippingPrice = document.querySelector('.js-checkout-shipping-price');
        this.shippingVat = document.querySelector('.js-checkout-shipping-vat');
        this.total = document.querySelector('.js-checkout-total');
        this.totalTax = document.querySelector('.js-checkout-total-tax');
        this.debugState = document.querySelector('.js-shipping-debug-state');

        this.bindDebugControls();

        // Only a cart nobody has quoted yet needs asking. Every other state was
        // already resolved from the cache while the page rendered.
        if (this.container.dataset.shippingState === 'pending') {
            this.fetchQuote('/api/v1/cart/shipping-quote');
        }
    }

    private bindDebugControls(): void {
        document.querySelector('.js-shipping-debug-replay')?.addEventListener('click', () => {
            // Forget the cached answer, put the page back into the pending state, then
            // ask again, so the spinner is visible for a real round trip.
            this.applyState('pending');
            this.fetchQuote('/api/v1/cart/shipping-debug', { replay: '1' });
        });

        document.querySelector('.js-shipping-debug-force')?.addEventListener('click', () => {
            this.fetchQuote('/api/v1/cart/shipping-debug', { forceOnRequest: '1' });
        });
    }

    private fetchQuote(path: string, body: Record<string, string> = {}): void {
        const apiBaseUrl = document.body.dataset.apiBaseUrl ?? '/';

        fetch(`${apiBaseUrl.replace(/\/$/, '')}${path}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify(body),
        })
            .then((response) => response.json())
            .then((data: ShippingQuoteResponse) => this.render(data))
            .catch((error) => {
                console.error('Shipping quote failed:', error);

                // The cost could not be established, so treat it exactly as the
                // interface saying it cannot price the cart (AK12).
                this.applyState('on_request');
            });
    }

    private render(data: ShippingQuoteResponse): void {
        if (this.shippingPrice) {
            this.shippingPrice.innerText = data.shippingLabel;
        }

        if (this.shippingVat) {
            this.shippingVat.innerText = data.shippingVatLabel ?? '';
        }

        if (this.total && data.total) {
            this.total.innerText = data.total;
        }

        if (this.totalTax && data.totalTax) {
            this.totalTax.innerText = data.totalTax;
        }

        this.applyState(data.state);
    }

    private applyState(state: ShippingQuoteResponse['state']): void {
        if (!this.container) {
            return;
        }

        this.container.dataset.shippingState = state;

        const pending = state === 'pending';
        const onRequest = state === 'on_request';

        if (this.debugState) {
            this.debugState.innerText = state;
        }

        // While the cost is unknown there is nothing to choose between, so the whole
        // list is replaced by the loading line rather than shown disabled (AK9).
        this.loading?.classList.toggle('d-none', !pending);
        this.options?.classList.toggle('d-none', pending);


        if (pending) {
            if (this.shippingPrice) {
                this.shippingPrice.innerText = this.shippingPrice.dataset.pendingText ?? '...';
            }

            if (this.shippingVat) {
                this.shippingVat.innerText = '';
            }
        }

        // Vorkasse carries data-requires-shipping-cost="0" and is available in every
        // state the customer can act on. Everything else, PayPal included, is
        // withheld while the cost is on request, and disabled as well as hidden, so
        // unhiding it in the browser is not enough to submit it. The payment itself
        // is refused server-side too (AK12).
        document
            .querySelectorAll<HTMLElement>('.js-payment-option[data-requires-shipping-cost="1"], .js-payment-option-rule[data-requires-shipping-cost="1"]')
            .forEach((element) => {
                element.classList.toggle('d-none', onRequest);

                element.querySelectorAll<HTMLInputElement>('input.input-radio-payment-method').forEach((input) => {
                    input.disabled = onRequest;

                    if (onRequest && input.checked) {
                        input.checked = false;
                    }
                });
            });

        // Falling back to Vorkasse when the option the customer had picked just went
        // away, so the form never posts without a gateway.
        if (!pending) {
            const prepayment = document.querySelector<HTMLInputElement>(
                '.js-payment-option[data-requires-shipping-cost="0"] input.input-radio-payment-method'
            );

            if (prepayment && !document.querySelector('input.input-radio-payment-method:checked')) {
                prepayment.checked = true;
                prepayment.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }
}
